import {
  createContext,
  ReactElement,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from "react";
import { USER_LOGIN, USER_LOGOUT } from "./constants";
import { useQuery } from "react-query";
import { useQueryClient } from "react-query";
import { AuthService } from "../services/AuthService";
import { useMutations } from "../hooks/useMutations";
import { noop } from "../utils/helpers";
import Cookies from "js-cookie";

export const AuthContext = createContext({
  userDetails: null,
  isUserDetailsLoading: false,
  handleLogout: noop,
  refetchUserDetails: noop, // Add this for manual refresh
});

export const authReducer = (
  state: any,
  action: { type: any; payload?: any },
) => {
  switch (action.type) {
    case USER_LOGIN:
      return {
        ...state,
        userDetails: action.payload,
        isUserDetailsLoading: false,
      };

    case USER_LOGOUT:
      return { ...state, userDetails: null };
    default:
      return state;
  }
};

// Helper function to check if SaaS token is valid
const isValidSaaSToken = (token: string | null): boolean => {
  return token !== null && token.trim() !== '' && token !== 'null' && token !== 'undefined';
};

export const AuthContextProvider = ({
  children,
}: {
  children: ReactElement;
}) => {
  const [state, dispatch] = useReducer(authReducer, {
    userDetails: null,
    isUserDetailsLoading: true,
  });
  
  const queryClient = useQueryClient();
  
  // State to track authentication changes
  const [authState, setAuthState] = useState({
    hasSaaSToken: localStorage.getItem('token'),
  });
  
  // Force re-evaluation trigger
  const [forceRefresh, setForceRefresh] = useState(0);
  
  // Check authentication type
  const isSaaSRoute = window.location.pathname.startsWith('/saas');
  const saasToken = authState.hasSaaSToken;
  const hasSaaSToken = isValidSaaSToken(saasToken);
  
  // Determine authentication type and service to use
  // For SaaS users, we need both a SaaS route AND a valid token
  const isSaasUser = isSaaSRoute && hasSaaSToken;
  const isEndUser = !isSaaSRoute;
  
  // Check for authentication changes periodically
  useEffect(() => {
    const checkAuthChanges = () => {
      const currentSaaSToken = localStorage.getItem('token');
      
      if (currentSaaSToken !== authState.hasSaaSToken) {
        
        setAuthState({
          hasSaaSToken: currentSaaSToken,
        });
        
        // Force queries to refetch
        setForceRefresh(prev => prev + 1);
      }
    };
    
    // Check immediately
    checkAuthChanges();
    
    // Check every 1 second for changes
    const interval = setInterval(checkAuthChanges, 1000);
    
    return () => clearInterval(interval);
  }, [authState.hasSaaSToken]);
  
  // Clear user profile query cache when conditions change
  useEffect(() => {
    if (!isEndUser && !isSaasUser) {
      queryClient.removeQueries(['userProfile']);
      queryClient.removeQueries(['saasUserProfile']);
    }
  }, [isEndUser, isSaasUser, queryClient]);
  
  // Query for end users (session-based)
  const { data: endUserData, error: endUserError, isLoading: endUserLoading, refetch: refetchEndUser } = useQuery(
    [`userProfile`, forceRefresh], // Include forceRefresh in query key
    AuthService.getUserDetails,
    {
      staleTime: 360_000,
      cacheTime: 360_000,
      enabled: isEndUser,
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  // Query for SaaS users (token-based)
  const { data: saasUserData, error: saasUserError, isLoading: saasUserLoading, refetch: refetchSaasUser } = useQuery(
    [`saasUserProfile`, forceRefresh], // Include forceRefresh in query key
    AuthService.getSaasUserDetails,
    {
      staleTime: 360_000,
      cacheTime: 360_000,
      enabled: isSaasUser,
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  // Combine data from both queries
  const data = endUserData || saasUserData;
  const error = endUserError || saasUserError;
  const isLoading = endUserLoading || saasUserLoading;

  const { mutateAsync: logout } = useMutations(AuthService.logout);
  const handleLogout = useCallback(async () => {
    try {
      await logout();
      dispatch({
        type: USER_LOGOUT,
      });
    } catch (error) {
      console.error("logout failed");
    }
  }, []);
  
  // Manual refetch function
  const refetchUserDetails = useCallback(() => {
    setForceRefresh(prev => prev + 1);
    if (isEndUser) {
      refetchEndUser();
    } else if (isSaasUser) {
      refetchSaasUser();
    }
  }, [isEndUser, isSaasUser, refetchEndUser, refetchSaasUser]);
  
  useEffect(() => {
    
    dispatch({
      type: USER_LOGIN,
      payload: data?.hasOwnProperty("error") ? undefined : data,
    });
  }, [data]);

  return (
    <AuthContext.Provider value={{ ...state, dispatch, handleLogout, refetchUserDetails }}>
      {children}
    </AuthContext.Provider>
  );
};